import { Button, EmptyState, Stack, Text } from "@amtana/design-system";
import {
  currentLanguage,
  enableUrlLanguage,
  pathWithoutLanguage,
  readWords,
  syncDocumentLanguage,
  translate,
  useActiveLanguage,
  useTranslations,
} from "@amtana/i18n";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
  QueryErrorResetBoundary,
} from "@tanstack/react-query";
import { Component, createElement, StrictMode, Suspense, useContext, useEffect, useMemo } from "react";
import type { ComponentType, ReactNode } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";

import { screenGate } from "./access-gate.ts";
import { BROWSER_CATALOGS } from "./browser-copy.ts";
import { explainError, referenceText, setErrorCopy } from "./errors.ts";
import { LentContext } from "./lent.tsx";
import { addressLanguage, install, match, notFoundRouteOf, resolveRoute } from "./route-table.ts";
import type { Installed } from "./route-table.ts";
import { useLocationPath } from "./router.ts";
import { ScreenLanguagesContext } from "./screen-languages.ts";
import { recoverFromStaleBuild, watchForNewBuild } from "./stale-build.ts";
import { noticeTermsRefusal } from "./terms-refusal.ts";
import {
  FlagScope,
  FlagValuesContext,
  NavigationContext,
  ParamsContext,
  PermissionsContext,
} from "./ui-context.tsx";
import type { BrowserModule, LayoutLoader, ScreenLoader } from "./web-module.ts";

export {
  useFlag,
  useFlagValues,
  useNavigation,
  usePermissions,
  useRequiredParam,
  useRouteParams,
} from "./ui-context.tsx";
export type { RenderedPage } from "./render-page.tsx";

// The browser app's whole involvement: createUi({ mount }).withModules(list).render()/hydrate().
// Providers, routing, layouts, lazy screens, navigation and the error boundary are machinery and
// live here, never in an app; a server render is `@amtana/browser/server`, the shared context is
// `ui-context.tsx`.

type BoundaryProps = {
  readonly children: ReactNode;
  /** The title when the failure has no copy of its own: what did not load. */
  readonly fallbackTitle: string;
  /** Clears the failed reads so "Try again" fetches them anew. */
  readonly onReset: () => void;
};

/**
 * Catches what a screen or a layout throws, including a read that failed
 * before it had data (packages/module ADR-001, point 6): it explains the
 * failure in the user's words, offers "Try again", and gives the reference
 * when the failure is ours. What it caught goes to the console with that
 * reference, so a report can be matched to the server's logs.
 */
class ErrorBoundary extends Component<BoundaryProps, { error: unknown; failed: boolean }> {
  override state = { error: undefined as unknown, failed: false };

  static getDerivedStateFromError(error: unknown): { error: unknown; failed: boolean } {
    return { error, failed: true };
  }

  override componentDidCatch(error: unknown): void {
    // A screen of an older build whose chunk the new release no longer serves: reload, once.
    if (recoverFromStaleBuild(error)) return;
    const { reference } = explainError(error, this.props.fallbackTitle);
    // The one place the browser logs: a boundary is the end of the line for what it caught.
    console.error("[amtana] a view failed", { reference, error });
  }

  #retry = (): void => {
    this.props.onReset();
    this.setState({ error: undefined, failed: false });
  };

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    const explained = explainError(this.state.error, this.props.fallbackTitle);
    return (
      <main role="alert">
        <EmptyState
          icon="alert-circle"
          title={explained.title}
          description={explained.description}
          action={
            <Stack gap={2} align="center">
              <Button onClick={this.#retry}>
                {translate(BROWSER_CATALOGS, currentLanguage(), { key: "tryAgain" })}
              </Button>
              {explained.reference ? (
                <Text size="xs" tone="subtle">
                  {referenceText(explained.reference)}
                </Text>
              ) : null}
            </Stack>
          }
        />
      </main>
    );
  }
}

function Guarded({
  children,
  fallbackTitle,
}: {
  readonly children: ReactNode;
  readonly fallbackTitle: string;
}) {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary fallbackTitle={fallbackTitle} onReset={reset}>
          {children}
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}

/**
 * A screen or layout's own component, once its loader has resolved, keyed by the loader
 * itself. `Routes` renders straight from here with no `Suspense` when it can: a prerendered
 * page has no dehydrated boundary for `lazy()` to resume from, so hydrating a screen that is
 * still loading always mismatches (React #418). `hydrate()` fills this in for the one screen
 * (and layout) the current URL needs before it ever calls `hydrateRoot`; every other screen
 * still loads lazily, behind `Suspense`, for a plain client-side navigation.
 */
const resolvedScreens = new WeakMap<ScreenLoader, ComponentType>();
const resolvedLayouts = new WeakMap<LayoutLoader, ComponentType<{ readonly children: ReactNode }>>();

/** Loads the current URL's own screen (and layout) before `hydrate()` calls `hydrateRoot`. */
async function preloadForHydration(installed: Installed): Promise<void> {
  const resolved = resolveRoute(installed, window.location.pathname);
  if (!resolved) return;
  const { screen } = resolved.route;
  const { default: Screen } = await screen.load();
  resolvedScreens.set(screen.load, Screen);
  const layoutLoader = screen.layout ? installed.layoutLoaders.get(screen.layout) : undefined;
  if (layoutLoader) {
    const { default: Layout } = await layoutLoader();
    resolvedLayouts.set(layoutLoader, Layout);
  }
}

function Routes({ installed }: { installed: Installed }) {
  // The website's "/es/" prefix (ADR-027 point 5) names a language, not a screen: no module
  // declares it, so matching strips it first. The platform's paths never carry it, so this is
  // a no-op there.
  const address = useLocationPath();
  const path = pathWithoutLanguage(address);
  const flags = useContext(FlagValuesContext);
  const permissions = useContext(PermissionsContext);
  const found = match(installed.routes, path, addressLanguage(address));

  // ADR-006 §7, S12.01: a gated screen never renders before its flag and permissions arrive
  // (nothing, so it never flashes), and the not-found screen once they have arrived without
  // what it needs, exactly like opening a path no module declared.
  const gate = found ? screenGate(found.route.screen, { flags, permissions }) : "pending";
  const notFound = notFoundRouteOf(installed.routes);
  const resolved =
    !found || gate === "pending"
      ? undefined
      : gate === "allowed"
        ? found
        : notFound
          ? { route: notFound, params: {} }
          : undefined;

  const language = useActiveLanguage();
  const t = useTranslations(BROWSER_CATALOGS);
  const declared = resolved?.route.screen.title;
  const title = declared === undefined ? undefined : readWords(declared, language);
  useEffect(() => {
    if (title) document.title = title;
  }, [title]);
  if (!resolved) return null;

  // Already loaded (preloaded for hydration, or a screen revisited since): render it directly,
  // with no `Suspense` to mismatch what a prerendered page sent. Still loading: the ordinary
  // lazy screen, behind `Suspense`, for a plain client-side navigation.
  const ResolvedScreen = resolvedScreens.get(resolved.route.screen.load);
  const screen = (
    <Guarded key={path} fallbackTitle={t("pageDidNotLoad")}>
      {ResolvedScreen ? (
        createElement(ResolvedScreen)
      ) : (
        <Suspense fallback={null}>{createElement(resolved.route.view)}</Suspense>
      )}
    </Guarded>
  );

  const layoutName = resolved.route.screen.layout;
  const layoutLoader = layoutName ? installed.layoutLoaders.get(layoutName) : undefined;
  const Layout = layoutName ? installed.layouts.get(layoutName) : undefined;
  const ResolvedLayout = layoutLoader ? resolvedLayouts.get(layoutLoader) : undefined;

  return (
    <ParamsContext.Provider value={resolved.params}>
      <ScreenLanguagesContext.Provider value={resolved.route.screen.languages}>
        {Layout ? (
          <Guarded fallbackTitle={t("appDidNotLoad")}>
            {ResolvedLayout ? (
              createElement(ResolvedLayout, null, screen)
            ) : (
              <Suspense fallback={null}>{createElement(Layout, null, screen)}</Suspense>
            )}
          </Guarded>
        ) : (
          screen
        )}
      </ScreenLanguagesContext.Provider>
    </ParamsContext.Provider>
  );
}

function UiRoot({ installed }: { installed: Installed }) {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
        // ADR-032, S37.07: any door refusing for the Terms sends the person to the gate.
        queryCache: new QueryCache({ onError: noticeTermsRefusal }),
        mutationCache: new MutationCache({ onError: noticeTermsRefusal }),
      }),
    [],
  );

  return (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <FlagScope installed={installed}>
          <NavigationContext.Provider value={installed.navigation}>
            <LentContext.Provider value={installed.lends}>
              <Routes installed={installed} />
            </LentContext.Provider>
          </NavigationContext.Provider>
        </FlagScope>
      </QueryClientProvider>
    </StrictMode>
  );
}

export function createUi(options: { mount: string }) {
  return {
    withModules(modules: readonly BrowserModule[]) {
      const installed = install(modules);
      setErrorCopy(installed.errors);
      return {
        /** Renders the app into the mount element; returns the unmount, which an app never
         * needs and a test rendering one screen after another does (S33.08). */
        render(): () => void {
          const element = document.getElementById(options.mount);
          if (!element) throw new Error(`createUi: no element with id "${options.mount}".`);
          // D952: <html lang> is right before the first paint, not only after it
          // (`useActiveLanguage`'s own effect keeps it in step from here on).
          syncDocumentLanguage();
          watchForNewBuild();
          const root = createRoot(element);
          root.render(<UiRoot installed={installed} />);
          return () => root.unmount();
        },
        /**
         * Takes over a prerendered page with `hydrateRoot`, matching what the server sent with
         * no mismatch; falls back to `createRoot` on the empty shell `pnpm dev` serves, which
         * never ran a server render (ADR-027 point 3). Loads the current URL's own screen (and
         * layout) first: a prerendered page carries no dehydrated `Suspense` boundary for a
         * still-loading lazy screen to resume from, so hydrating one always mismatches
         * (React #418) unless it is already resolved before `hydrateRoot` runs. Returns the
         * unmount, as `render()` does, for a test to stop the page's timers before it ends.
         */
        async hydrate(): Promise<() => void> {
          const element = document.getElementById(options.mount);
          if (!element) throw new Error(`createUi: no element with id "${options.mount}".`);
          watchForNewBuild();
          if (element.hasChildNodes()) {
            // A prerendered page: the URL already answered the language (ADR-027 point 5), so
            // the device's own country never silently switches it, unlike the platform's own
            // sign-in pages (S10.08), which never call `hydrate()` at all.
            enableUrlLanguage();
            await preloadForHydration(installed);
            const root = hydrateRoot(element, <UiRoot installed={installed} />);
            return () => root.unmount();
          }
          syncDocumentLanguage();
          const root = createRoot(element);
          root.render(<UiRoot installed={installed} />);
          return () => root.unmount();
        },
      };
    },
  };
}
